/**
 * KartotekaViewListBuilder — builder wizualny WIDOKU LISTY dla nazwanego widoku biblioteki
 * kartoteki (`kartoteka_views`, viewType='list'). Reużywa istniejący `SystemModuleListBuilderPage`
 * (zakładki Kolumny / Filtry / Kanban / Strona / …) osadzony w scope='system'.
 *
 * Analog `KartotekaViewFormBuilder` dla listy. Formularz ma adapter do `ModuleBuilderPage`;
 * lista nie ma adaptera, więc podpięcie idzie przez `ReferenceListContext`:
 *  - `initialListConfig` = aktualny config widoku (seed — `useTableLayout` bierze go jako
 *    aktywny layout w trybie reference-view, zamiast pobierać layouty modułu systemowego),
 *  - `onListConfigSave` = interceptor zapisu → `updateKartotekaLibraryView` (PATCH widoku).
 * Po każdym zapisie aktualizujemy `currentConfig`, żeby przełączenie zakładki nie zasiało
 * starego configu (brak clobberu między zakładkami).
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  kartotekaExcludedListFieldSlugs,
  type KartotekaViewDto,
  type ListConfig,
  normalizeKartotekaListConfig,
} from '@saas/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';

import { Button } from '@/components/Button';
import {
  ReferenceListContext,
  type ReferenceListContextValue,
} from '@/contexts/ReferenceListContext';
import { useToast } from '@/contexts/ToastContext';
import {
  type KartotekaBuilderField,
  type KartotekaDefaultListConfig,
  type KartotekaIdentityRef,
  kartotekaLibraryViewsQueryKey,
  updateKartotekaLibraryView,
} from '@/lib/kartoteki/kartoteka-definition';
import { SystemModuleListBuilderPage } from '@/pages/system-modules/SystemModuleListBuilderPage';

interface KartotekaViewListBuilderProps {
  /** Tożsamość kartoteki — tu obsługujemy systemową (slug); Kreatora edytuje builder menu. */
  identity: KartotekaIdentityRef;
  view: KartotekaViewDto;
  fields: KartotekaBuilderField[];
  /**
   * Czy `fields` jest katalogiem POTWIERDZONYM (zapytanie definicji w `KartotekaFieldsManager`
   * ma dane), nie fallbackiem `[]` w trakcie ładowania. Gdy `false` — `availableFields` w
   * kontekście jest `undefined`, żeby soft-heal w `ListColumnsPanel` (`allowedColumnIds =
   * availableFields ? ... : undefined`) NIE potraktował stanu ładowania jak „katalog
   * potwierdzony pusty" i nie wyciął WSZYSTKICH kolumn (core+ext) z zapisanego list_config,
   * gdyby user kliknął „Zapisz" zanim katalog pól się wczytał.
   */
  fieldsReady: boolean;
  defaultListConfig?: KartotekaDefaultListConfig;
  onBack: () => void;
  /** Zamiast zapisu do biblioteki — oddaj config wołającemu (zapis razem z menu modułu). */
  onSaveConfig?: (config: ListConfig) => void;
}

export function KartotekaViewListBuilder({
  identity,
  view,
  fields,
  fieldsReady,
  defaultListConfig,
  onBack,
  onSaveConfig,
}: KartotekaViewListBuilderProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  // Aktualny config widoku — aktualizowany po każdym zapisie zakładki, żeby seed kolejnej
  // zakładki niósł najświeższy stan (nie oryginał sprzed edycji).
  // Stary układ kartoteki systemowej z `company_id`/`category_id` → kolumny klienta (Firma,
  // Kategoria) — inaczej wisiałby surowy slug, a zapis wyciąłby kolumnę bez śladu.
  const [currentConfig, setCurrentConfig] = useState<ListConfig>(
    (view.config && identity.kind === 'system'
      ? normalizeKartotekaListConfig(identity.key, view.config as ListConfig)
      : (view.config as ListConfig | null)) ??
      (defaultListConfig as unknown as ListConfig) ??
      ({
        columns: [],
        defaultSort: null,
        defaultFilters: [],
        formattingRules: [],
        quickFilters: [],
        defaultViewMode: 'list',
      } as unknown as ListConfig),
  );

  // Kartoteka z kreatora (`new_table`) ma fizyczną nazwę/numer rekordu — to realne kolumny.
  const builtinColumnsAvailable = identity.kind === 'creator';
  // Kartoteka systemowa: bez pól, które lista klienta rysuje własną kolumną (`company_id` →
  // Firma, `category_id` → Kategoria) — inaczej kolumna z ITE nie pojawi się u klienta.
  const listFields = useMemo(() => {
    if (identity.kind !== 'system') return fields;
    const excluded = new Set(kartotekaExcludedListFieldSlugs(identity.key));
    return fields.filter((f) => !excluded.has(f.slug));
  }, [fields, identity]);
  // Katalog dla zakładek kreatora (nazwy pól, typy). ZNANE OGRANICZENIA: pola pochodzą z
  // definicji kartoteki w ADM (`GET /admin/kartoteki/{slug}/definition`, tabela
  // `system_kartoteka_fields`), która:
  //  1) NIE niesie flagi `is_filterable` (otwarty dług, patrz `lib/system-kartoteka-apply.ts`:
  //     centralne pola są u tenantów filtrowalne domyślnie). Nie ustawiamy więc `isFilterable` —
  //     brak flagi = dozwolone: zakładka Kalendarz filtruje tu pola dat tylko po TYPIE, bez
  //     wykluczania pól niefiltrowalnych (w przeciwieństwie do samodzielnego kreatora listy, który
  //     ma flagę z katalogu tenanta);
  //  2) NIE niesie flagi „Pokaż na liście" przy polach — katalog to WSZYSTKIE pola definicji, także
  //     te, których lista klienta nie narysuje (a kalendarz u klienta szuka pól wśród kolumn, które
  //     lista umie narysować). Domyślnego układu listy z serwera (`defaultListConfig`) NIE da się tu
  //     użyć jako zbioru pól listowalnych: zawiera tylko pola rdzenne z „Pokaż na liście" — pól
  //     dodanych i puli klasy (np. `date_from` w „specjalne") w nim nie ma, a w oknie widoku obcej
  //     kartoteki jest dodatkowo przycięty do jednej kolumny. Dlatego `catalogListFlagUnknown`
  //     (niżej) włącza w zakładce Kalendarz ogólną podpowiedź zamiast zawężenia.
  // Naprawa obu wymaga rozszerzenia API definicji o flagi.
  const availableFields = useMemo(
    () =>
      fieldsReady
        ? [
            ...(builtinColumnsAvailable
              ? [
                  {
                    id: 'record_name',
                    name: t('moduleTemplateEditor.layoutDesigner.builtinColumns.recordName'),
                  },
                  {
                    id: 'record_number',
                    name: t('moduleTemplateEditor.layoutDesigner.builtinColumns.recordNumber'),
                  },
                  // Kategoria modułu (u klienta zawsze jest „Ogólne") — decyzja 2026-10-05.
                  { id: 'category', name: t('tenant.recordCategories.picker.label') },
                ]
              : []),
            ...listFields.map((f) => ({ id: f.slug, name: f.name, fieldType: f.fieldType })),
          ]
        : undefined,
    [listFields, fieldsReady, builtinColumnsAvailable, t],
  );

  const { mutate: saveListConfig } = useMutation({
    mutationFn: (listConfig: ListConfig) =>
      updateKartotekaLibraryView(identity, view.slug, {
        name: view.name,
        viewType: 'list',
        config: listConfig as unknown as Record<string, unknown>,
        sortOrder: view.sortOrder,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: kartotekaLibraryViewsQueryKey(identity) });
    },
    onError: () => {
      toast.show({
        variant: 'error',
        title: t('moduleTemplateEditor.referencePanel.saveListViewError'),
      });
    },
  });

  // Wartość kontekstu MUSI być stabilna — inaczej seed w `useTableLayout` (memo zależny od
  // referenceCtx) przelicza się co render i kasuje edycję w toku / powoduje thrashing.
  // Zmienia tożsamość tylko gdy zmieni się config (po zapisie) albo pola/domyślny config.
  const contextValue = useMemo<ReferenceListContextValue>(
    () => ({
      kartotekaFields: listFields,
      availableFields,
      // Katalog z definicji kartoteki nie wie, które pola mają „Pokaż na liście" — patrz komentarz
      // przy `availableFields`; zakładka Kalendarz dopisuje wtedy ogólną podpowiedź.
      catalogListFlagUnknown: true,
      initialListConfig: currentConfig,
      kartotekaDefaultListConfig: defaultListConfig,
      onViewConfigPersisted: () => {},
      onListConfigSave: (listConfig) => {
        setCurrentConfig(listConfig);
        if (onSaveConfig) onSaveConfig(listConfig);
        else saveListConfig(listConfig);
      },
      // Kartoteka SYSTEMOWA nie ma fizycznej kolumny nazwy/numeru rekordu (built-iny
      // record_name/record_number nie są realne); kartoteka z KREATORA — ma.
      builtinColumnsAvailable,
    }),
    [
      listFields,
      availableFields,
      currentConfig,
      defaultListConfig,
      saveListConfig,
      onSaveConfig,
      builtinColumnsAvailable,
    ],
  );

  return (
    <div data-testid="kartoteka-view-list-builder">
      <div className="mb-3 flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<ArrowLeft className="h-4 w-4" />}
          onClick={onBack}
          data-testid="kartoteka-list-view-builder-back"
        >
          {t('moduleTemplateEditor.referencePanel.listViewBuilderBack')}
        </Button>
        <span className="text-sm font-medium text-text-primary">{view.name}</span>
      </div>
      <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
        <ReferenceListContext.Provider value={contextValue}>
          <SystemModuleListBuilderPage
            scope="system"
            embeddedMode
            code={identity.key}
            creatorKartoteka={identity.kind === 'creator'}
            hideAddWidget
          />
        </ReferenceListContext.Provider>
      </div>
    </div>
  );
}
